import { HStack } from '@chakra-ui/react';
import {
   BlueskyLogo,
   FacebookLogo,
   InstagramLogo,
   MastodonLogo,
   RepairOrgLogo,
   RepairEULogo,
   ThreadsLogo,
   TwitterLogo,
   TiktokLogo,
   YoutubeLogo,
} from '@ifixit/icons';
import { memo } from 'react';
import type { SVGProps, ReactElement } from 'react';
import { FooterLink } from './Shared';
import { type LocaleCode } from '@ifixit/i18n';

type IconType = (props: SVGProps<SVGSVGElement>) => ReactElement;
type IconMap = {
   'default': IconType;
   'eu': IconType;
};

const socialMediaAccounts = {
   tiktok: {
      url: {
         'default': 'https://tiktok.com/@ifixit.com',
         'eu': 'https://www.tiktok.com/@ifixiteurope',
      },
      icon: TiktokLogo,
      ariaLabel: 'TikTok',
   },
   facebook: {
      url: {
         'default': 'https://www.facebook.com/iFixit/',
         'eu': 'https://www.facebook.com/iFixitEU',
         'fr': 'https://www.facebook.com/iFixitFR/',
      },
      icon: FacebookLogo,
      ariaLabel: 'Facebook',
   },
   twitter: {
      url: {
         'default': 'https://twitter.com/ifixit',
         'eu': 'https://x.com/ifixitde',
         'fr': 'https://x.com/ifixitfr',
      },
      icon: TwitterLogo,
      ariaLabel: 'Twitter',
   },
   instagram: {
      url: {
         'default': 'https://www.instagram.com/ifixit/',
         'eu': 'https://www.instagram.com/ifixiteurope/',
         'fr': 'https://www.instagram.com/ifixitfrance/',
      },
      icon: InstagramLogo,
      ariaLabel: 'Instagram',
   },
   youtube: {
      url: {
         'default': 'https://www.youtube.com/user/iFixitYourself',
      },
      icon: YoutubeLogo,
      ariaLabel: 'YouTube',
   },
   mastodon: {
      url: {
         'default': 'https://mastodon.social/@iFixit',
      },
      icon: MastodonLogo,
      ariaLabel: 'Mastodon',
   },
   bluesky: {
      url: {
         'default': 'https://bsky.app/profile/ifixit.com',
         'eu': 'https://bsky.app/profile/ifixiteurope.bsky.social',
      },
      icon: BlueskyLogo,
      ariaLabel: 'Bluesky',
   },
   threads: {
      url: {
         'default': 'https://www.threads.com/@ifixit',
         'eu': 'https://www.threads.com/@ifixiteurope',
         'fr': 'https://www.threads.com/@ifixitfrance',
      },
      icon: ThreadsLogo,
      ariaLabel: 'Threads',
   },
   repairorg: {
      url: {
         'default': 'https://repair.org',
         'eu': 'https://repair.eu',
      },
      icon: {
         'default': RepairOrgLogo,
         'eu': RepairEULogo,
      },
      ariaLabel: 'The Repair Association',
   },
};

export const SocialMediaSection = memo(function SocialMediaSection({
   locale,
}: {
   locale: LocaleCode;
}) {
   return (
      <HStack spacing={4} flexWrap="wrap" justify="center">
         {Object.entries(socialMediaAccounts).map(([key, value]) => (
            <FooterLink
               key={key}
               aria-label={value.ariaLabel}
               href={getSocialUrl(key as keyof typeof socialMediaAccounts, locale)}
               icon={getIcon(key as keyof typeof socialMediaAccounts, value.icon, locale)}
               customColor={'gray.400'}
            />
         ))}
      </HStack>
   );
});

/**
 * Get the appropriate social media URL based on the platform key and store locale.
 * For fr-fr locale, try to use a fr url if available, otherwise fall through to eu.
 */
function getSocialUrl(key: keyof typeof socialMediaAccounts, localeCode: LocaleCode): string {
   const urlMap = socialMediaAccounts[key].url as Record<string, string>;
   const isEuropean = isEU(localeCode);
   const isFrench = localeCode === 'fr-fr';

   if (isFrench && urlMap['fr']) {
      return urlMap['fr'];
   }

   if (isEuropean && urlMap['eu']) {
      return urlMap['eu'];
   }

   return urlMap['default'];
}

function getIcon(
   key: keyof typeof socialMediaAccounts,
   icon: IconType | IconMap,
   locale: LocaleCode
): IconType {
   if (key !== 'repairorg') {
      return icon as IconType;
   }

   const iconMap = icon as IconMap;
   return isEU(locale) ? iconMap['eu'] : iconMap['default'];
}

function isEU(localeCode: LocaleCode): boolean {
   const euLocales = ['en-gb', 'en-eu', 'fr-fr', 'de-de', 'it-it'];
   return euLocales.includes(localeCode);
}
